import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AtualizarMaterialEntrada,
  AtualizarPastaMaterialEntrada,
  MaterialDto,
  MensagemDto,
  PastaMaterialDto,
} from '@crm/shared'
import { api, enviarArquivo } from './cliente'
import { invalidarChat } from './hooks-chat'

/** Biblioteca de materiais: o acervo que a equipe manda de dentro da conversa. */

const CHAVE = ['materiais']

export function useMateriais(habilitado = true) {
  return useQuery({
    queryKey: CHAVE,
    queryFn: () => api<PastaMaterialDto[]>('/api/materiais'),
    enabled: habilitado,
    // O acervo muda quando o dono sobe arquivo, e não a cada minuto.
    staleTime: 5 * 60_000,
  })
}

export function useCriarPasta() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (nome: string) =>
      api<PastaMaterialDto>('/api/materiais/pastas', { metodo: 'POST', corpo: { nome } }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useRenomearPasta() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string } & AtualizarPastaMaterialEntrada) =>
      api(`/api/materiais/pastas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useExcluirPasta() {
  const cliente = useQueryClient()
  return useMutation({
    // `confirmar` é o segundo clique: sem ele, a API recusa pasta com arquivo
    // dentro e devolve quantos são, para a pergunta dizer o número certo.
    mutationFn: ({ id, confirmar }: { id: string; confirmar?: boolean }) =>
      api(`/api/materiais/pastas/${id}${confirmar ? '?confirmar=sim' : ''}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useSubirMaterial() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ pastaId, arquivo }: { pastaId: string; arquivo: File }) =>
      enviarArquivo<MaterialDto>(`/api/materiais/pastas/${pastaId}/arquivos`, arquivo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useEditarMaterial() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string } & AtualizarMaterialEntrada) =>
      api<MaterialDto>(`/api/materiais/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useExcluirMaterial() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/materiais/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE }),
  })
}

/** Manda para o cliente um material que já está na biblioteca. */
export function useEnviarMaterial() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, materialId }: { chamadoId: string; materialId: string }) =>
      api<MensagemDto>(`/api/chamados/${chamadoId}/materiais/${materialId}`, { metodo: 'POST' }),
    onSuccess: (_, variaveis) => invalidarChat(cliente, variaveis.chamadoId),
  })
}
